import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anon, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    // No admin token stored → treat as anonymous instead of firing a 401 request.
    if (!localStorage.getItem('fender_admin_token')) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/admin/login', { email, password });
      localStorage.setItem('fender_admin_token', data.access_token);
      localStorage.setItem('fender_admin_refresh', data.refresh_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const logout = async () => {
    const rt = localStorage.getItem('fender_admin_refresh');
    try { await api.post('/auth/logout', rt ? { refresh_token: rt } : {}); } catch { /* noop */ }
    localStorage.removeItem('fender_admin_token');
    localStorage.removeItem('fender_admin_refresh');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Permission check for the signed-in staff user; mirrors the server-side any-of guard.
export function useCan() {
  const { user } = useAuth();
  const perms = new Set(user?.permissions || []);
  return useCallback(
    (...codes) => perms.has('*') || codes.some((c) => perms.has(c)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );
}
